import { useState } from 'react';
import PosThemeForm from './PosThemeForm';
import PosProductSettingsForm from './PosProductSettingsForm';
import PosStoreDetailsForm from './PosStoreDetailsForm';
import PosPaymentMethodsForm from './PosPaymentMethodsForm';
import PosStaffAccessForm from './PosStaffAccessForm';
import { usePosSession } from '../context/PosSessionContext';
import { POS_CONFIG_TABS, POS_STORE_ADMIN_CONFIG_TABS } from '../constants/posConst';
import '../css/Pos.css';

/**
 * Config — this client's own settings, in tabs.
 *
 * The screen itself holds nothing but which tab is open: each form loads,
 * validates and saves its own keys, and both write to the same node with
 * update(), so saving one cannot wipe the other. Splitting them that way is
 * what lets a tab be added later without touching either existing form.
 *
 * @returns {JSX.Element}
 */
function PosConfigScreen() {
  const { isStoreAdmin } = usePosSession();
  // Staff Access is the store admin's alone; everyone else never sees the tab.
  const tabs = POS_CONFIG_TABS.filter((option) => isStoreAdmin || !POS_STORE_ADMIN_CONFIG_TABS.includes(option.key));
  const [tab, setTab] = useState(tabs[0].key);

  return (
    <div className="pos-config-screen">
      <div className="pos-conftabs" role="tablist" aria-label="Config sections">
        {tabs.map((option) => (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={tab === option.key}
            className={`pos-conftab${tab === option.key ? ' active' : ''}`}
            onClick={() => setTab(option.key)}
            title={option.description}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Mounted one at a time rather than hidden with CSS: each form seeds
          its fields from config on mount, so leaving a tab and coming back is
          what drops a half-finished edit — the same thing its Cancel does. */}
      {tab === 'products' && <PosProductSettingsForm />}
      {tab === 'store' && <PosStoreDetailsForm />}
      {tab === 'payments' && <PosPaymentMethodsForm />}
      {tab === 'theme' && <PosThemeForm />}
      {tab === 'staffAccess' && isStoreAdmin && <PosStaffAccessForm />}
    </div>
  );
}

export default PosConfigScreen;
